import { FolderSimple, MagnifyingGlass } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { Dialog as D } from 'radix-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from '@/components/ui/cx';
import { Spinner } from '@/components/ui/States';
import { useDebounced } from '@/hooks/useDebounced';
import { phaseVar } from '@/lib/domain-ui';
import { useProjects } from '@/lib/queries';

const MIN = 2;

/** Ctrl K paleta: pretraga projekata (šifra, klijent, mjesto). */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const dq = useDebounced(q.trim(), 150);
  const enabled = open && dq.length >= MIN;
  const { data, isFetching } = useProjects({ q: dq, pageSize: 8, sort: '-code' }, { enabled });
  const items = useMemo(() => (enabled ? (data?.items ?? []) : []), [enabled, data]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);
  useEffect(() => setIdx(0), [dq]);

  const go = (id: string) => {
    onOpenChange(false);
    void navigate({
      to: '/projekti/$projectId',
      params: { projectId: id },
      search: { tab: undefined, position: undefined, doc: undefined },
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const it = items[idx];
      if (it) go(it.id);
    }
  };

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-[100] bg-[rgba(10,27,51,.45)]" />
        <D.Content
          aria-describedby={undefined}
          className="fixed top-[12vh] left-1/2 z-[101] flex w-[min(640px,calc(100vw-32px))] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-card"
          onKeyDown={onKeyDown}
        >
          <D.Title className="sr-only">{t('panel:shell.search.label')}</D.Title>
          <div className="flex items-center gap-2.5 border-b border-line px-4">
            <MagnifyingGlass size={18} className="text-muted" />
            <input
              autoFocus
              type="search"
              role="combobox"
              aria-expanded={items.length > 0}
              aria-controls="cmdk-list"
              aria-activedescendant={items[idx] ? `cmdk-${items[idx].id}` : undefined}
              aria-label={t('panel:shell.search.label')}
              placeholder={t('panel:shell.search.placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-14 flex-1 border-0 bg-transparent text-[15px] text-text outline-none"
            />
            {isFetching ? <Spinner /> : null}
            <kbd className="rounded-[5px] border border-line2 px-1.5 py-0.5 text-[11px] font-medium text-muted">
              Esc
            </kbd>
          </div>
          <div
            id="cmdk-list"
            role="listbox"
            className="flex max-h-[380px] flex-col overflow-auto p-1.5"
          >
            {dq.length < MIN ? (
              <p className="m-0 px-3 py-6 text-center text-sm text-muted">
                {t('panel:shell.search.hint', { count: MIN })}
              </p>
            ) : items.length === 0 && !isFetching ? (
              <p className="m-0 px-3 py-6 text-center text-sm text-muted">
                {t('panel:shell.search.noResults', { query: dq })}
              </p>
            ) : (
              <>
                <span className="px-2.5 pt-2 pb-1.5 text-[11px] font-semibold tracking-[.08em] text-muted uppercase">
                  {t('panel:shell.search.groups.projects')}
                </span>
                {items.map((p, i) => (
                  <button
                    key={p.id}
                    id={`cmdk-${p.id}`}
                    role="option"
                    aria-selected={i === idx}
                    type="button"
                    onMouseEnter={() => setIdx(i)}
                    onClick={() => go(p.id)}
                    className={cx(
                      'grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded-md border-0 px-2.5 py-2 text-left text-text',
                      i === idx ? 'bg-bg2' : 'bg-transparent',
                    )}
                  >
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-chip text-accent">
                      <FolderSimple size={16} />
                    </span>
                    <span className="flex min-w-0 flex-col gap-px">
                      <span className="text-sm font-semibold">
                        {p.code} · {p.clientName}
                      </span>
                      <span className="truncate text-xs text-muted">
                        {[p.city, p.address].filter(Boolean).join(', ')}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-[7px] rounded-sm bg-bg2 px-2.5 py-1 text-xs font-medium">
                      <span
                        className="h-2 w-2 rounded-[2px]"
                        style={{ background: phaseVar(p.phase) }}
                      />
                      {t(`common:phase.${p.phase}`)}
                    </span>
                  </button>
                ))}
              </>
            )}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
